import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { handleCharacterLimitChange } from "@/lib/validation/characterLimit";
import { charLimitMessage } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import {
  useAllergyFormState,
  handleSubmit,
  severityOptions,
  AllergyForm,
  AllergyEditMode,
  severityNumberToString,
  ALLERGY_NAME_MAX_LENGTH,
  ALLERGY_TEXT_MAX_LENGTH,
} from "@/lib/allergyData";

interface AllergyModalProps {
  trigger: React.ReactNode;
  onSave: (allergy: AllergyForm) => void;
  editMode?: AllergyEditMode;
  isDarkMode?: boolean;
}

export function AllergyModal({
  trigger,
  onSave,
  editMode,
  isDarkMode = false,
}: AllergyModalProps) {
  const {
    open,
    setOpen,
    register,
    handleSubmit: handleFormSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useAllergyFormState(editMode);

  const severity = watch("severity");
  const isAtLimit = (value: string | undefined | null, max: number) =>
    (value || "").length >= max;
  const [allergyNameLimitError, setAllergyNameLimitError] = useState<
    string | undefined
  >();
  const allergyNameField = register("allergyName");

  const resetFormFields = () => {
    setAllergyNameLimitError(undefined);
    if (editMode) {
      reset({
        allergyName: editMode.allergyName || "",
        severity:
          typeof editMode.severity === "number"
            ? severityNumberToString[editMode.severity] || "mild"
            : editMode.severity,

        reaction: editMode.reaction || "",
        notes: editMode.notes || "",
      });
    } else {
      reset({
        allergyName: "",
        severity: "mild",
        reaction: "",
        notes: "",
      });
    }
  };

  useEffect(() => {
    resetFormFields();
  }, [editMode]);

  const onSubmit = (data: AllergyForm) => {
    // The limit message is informational only: handleCharacterLimitChange never lets the
    // value exceed the limit, so saving stays allowed while the message is shown.
    handleSubmit(data, onSave, setOpen, reset);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        setOpen(newOpen);
        if (!newOpen) resetFormFields();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        className={`sm:max-w-[500px] transition-colors duration-300 ${
          isDarkMode
            ? "bg-gray-900 border-gray-700"
            : "bg-white border-gray-200"
        }`}
      >
        <DialogHeader>
          <DialogTitle
            className={`text-lg font-semibold transition-colors duration-300 ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {editMode ? "Edit Patient Allergy" : "Add Patient Allergy"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleFormSubmit(onSubmit)} className="space-y-6">
          {/* Allergy Name */}
          <div className="space-y-2">
            <Label
              htmlFor="allergyName"
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Allergy Name *
            </Label>
            <Input
              id="allergyName"
              type="text"
              placeholder="e.g., Peanuts, Pollen, Penicillin"
              {...allergyNameField}
              onChange={(event) =>
                handleCharacterLimitChange(
                  event.target.value,
                  500,
                  () => allergyNameField.onChange(event),
                  setAllergyNameLimitError,
                )
              }
              // Caps the DOM value too; otherwise the uncontrolled input keeps the over-limit
              // text, react-hook-form picks it up on blur and the schema blocks "Add Allergy".
              maxLength={ALLERGY_NAME_MAX_LENGTH}
              className="w-full"
            />
            {allergyNameLimitError ||
            isAtLimit(watch("allergyName"), ALLERGY_NAME_MAX_LENGTH) ? (
              <p className="text-sm text-red-600">
                {allergyNameLimitError ||
                  charLimitMessage(ALLERGY_NAME_MAX_LENGTH)}
              </p>
            ) : (
              errors.allergyName && (
                <p className="text-sm text-red-600">
                  {errors.allergyName.message}
                </p>
              )
            )}
          </div>

          {/* Severity */}
          <div className="space-y-2">
            <Label
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Severity *
            </Label>
            <Select
              value={severity}
              onValueChange={(value: "mild" | "moderate" | "severe") =>
                setValue("severity", value)
              }
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {severityOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Reaction */}
          <div className="space-y-2">
            <Label
              htmlFor="reaction"
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Typical Reaction (optional)
            </Label>
            <Input
              id="reaction"
              type="text"
              placeholder="e.g., Rash, Sneezing, Swelling"
              {...register("reaction")}
              maxLength={ALLERGY_TEXT_MAX_LENGTH}
              className="w-full"
            />
            {(errors.reaction ||
              isAtLimit(watch("reaction"), ALLERGY_TEXT_MAX_LENGTH)) && (
              <p className="text-sm text-red-600">
                {isAtLimit(watch("reaction"), ALLERGY_TEXT_MAX_LENGTH)
                  ? charLimitMessage(ALLERGY_TEXT_MAX_LENGTH)
                  : errors.reaction?.message}
              </p>
            )}
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label
              htmlFor="notes"
              className={`text-sm font-medium transition-colors duration-300 ${
                isDarkMode ? "text-gray-200" : "text-gray-700"
              }`}
            >
              Additional Notes (optional)
            </Label>
            <Input
              id="notes"
              type="text"
              placeholder="Any additional information..."
              {...register("notes")}
              maxLength={ALLERGY_TEXT_MAX_LENGTH}
              className="w-full"
            />
            {(errors.notes ||
              isAtLimit(watch("notes"), ALLERGY_TEXT_MAX_LENGTH)) && (
              <p className="text-sm text-red-600">
                {isAtLimit(watch("notes"), ALLERGY_TEXT_MAX_LENGTH)
                  ? charLimitMessage(ALLERGY_TEXT_MAX_LENGTH)
                  : errors.notes?.message}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                resetFormFields();
                setOpen(false);
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 text-white bg-slate-900 hover:bg-slate-800"
            >
              {editMode ? "Update Allergy" : "Add Allergy"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function AddAllergyButton({
  onSave,
  editMode,
  trigger,
  isDarkMode = false,
  disabled = false,
}: {
  onSave: (allergy: AllergyForm) => void;
  editMode?: AllergyEditMode;
  trigger?: React.ReactNode;
  isDarkMode?: boolean;
  disabled?: boolean;
}) {
  return (
    <AllergyModal
      trigger={
        trigger || (
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            className={`p-0 h-auto font-normal transition-colors duration-300 ${
              isDarkMode
                ? "text-gray-400 hover:text-gray-200"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Allergy
          </Button>
        )
      }
      onSave={onSave}
      editMode={editMode}
      isDarkMode={isDarkMode}
    />
  );
}
